import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { scrollToId } from "./Reveal";

const LINES = [["Care that"], ["fits the way", "you"], ["actually", "live."]];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ words, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.15, delay: 0.35 + i * 0.13, ease }}
    >
      {words.map((w, j) => (
        <span key={j} className={j === 1 || (i === 2 && j === 0) ? "italic text-[#0E4D44]" : ""}>{w}{j < words.length - 1 ? " " : ""}</span>
      ))}
    </motion.span>
  </span>
);

const Pulse = () => (
  <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="absolute left-0 right-0 bottom-[3%] w-full h-24 pointer-events-none" aria-hidden="true">
    <motion.path
      d="M0 70 H380 L410 70 L430 30 L455 105 L480 10 L505 95 L525 70 H760 L780 70 L795 50 L812 82 L828 70 H1200"
      fill="none" stroke="#D96B43" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.55 }}
      transition={{ duration: 2.6, delay: 0.9, ease: "easeInOut" }}
    />
  </svg>
);

const TiltPortrait = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);
  const cardY = useTransform(scrollY, [0, 700], [0, -60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-portrait">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
          transition={{ duration: 1.5, delay: 0.5, ease }}
          className="relative aspect-[4/5] w-full overflow-hidden bg-[#DCE6E2]"
        >
          <motion.img style={{ y: imgY }} src="/images/doctor.jpg" alt="Welcon clinician" className="absolute inset-0 h-[115%] w-full object-cover object-[60%_20%] -top-[8%] scale-[1.05]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2B26]/35 via-transparent to-transparent" />
        </motion.div>
        <motion.div
          style={{ y: cardY, translateZ: 60 }}
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 1.5, ease }}
          className="absolute left-3 sm:-left-14 bottom-6 sm:bottom-10 glass rounded-2xl p-4 pr-6 w-[230px]"
          data-testid="hero-checkin-card"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A6660]">Your check-in</p>
          <div className="mt-3 grid grid-cols-3 gap-3 text-[#0B2B26]">
            {[["BP", "—/—"], ["Sugar", "—"], ["BMI", "—"]].map(([k, v]) => (
              <div key={k}><p className="text-xs text-[#4A6660]">{k}</p><p className="font-display text-xl">{v}</p></div>
            ))}
          </div>
          <p className="mt-3 text-xs text-[#4A6660]">Measured in-clinic, reviewed with you.</p>
        </motion.div>
        <motion.div
          style={{ translateZ: 90 }}
          initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 1.8, ease }}
          className="absolute -right-2 sm:-right-6 top-10 h-28 w-28 rounded-full bg-[#D96B43] text-[#FFF8F2] grid place-items-center spin-slow"
        >
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true">
            <defs><path id="circ" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
            <text fontSize="9.5" textLength="228" lengthAdjust="spacing" fill="#FFF8F2" fontFamily="JetBrains Mono, monospace"><textPath href="#circ">LIFESTYLE FIRST • CLINICALLY LED • </textPath></text>
          </svg>
          <span className="font-display text-3xl italic">W</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="relative min-h-[100svh] pt-32 sm:pt-36 pb-16 overflow-hidden grain">
    <div className="absolute -top-40 -right-40 h-[620px] w-[620px] rounded-full bg-[#DCE9E4] blur-3xl opacity-70" aria-hidden="true" />
    <Pulse />
    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.24em] text-[#22413B] flex items-center gap-2" data-testid="hero-location-tag">
          <MapPin className="h-3.5 w-3.5 text-[#D96B43]" /> Bhopal, Madhya Pradesh · Health Consultations LLP
        </motion.p>
        <h1 data-testid="hero-headline-title" className="mt-7 font-display text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[6.4rem] tracking-[-0.03em] text-[#0B2B26]">
          {LINES.map((w, i) => <MaskLine key={i} words={w} i={i} />)}
        </h1>
        <motion.p data-testid="hero-subtitle-description" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#33504A] leading-relaxed">
          Clinical consultations, preventive health guidance and sustainable wellness practices — brought together in one lifestyle-first programme for individuals and families.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease }} className="mt-10 flex flex-wrap gap-4">
          <button data-testid="hero-request-consultation-btn" onClick={() => scrollToId("book")} className="btn-primary group">
            Request a consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button data-testid="hero-explore-framework-btn" onClick={() => scrollToId("framework")} className="btn-ghost">How care works</button>
        </motion.div>
      </div>
      <div className="lg:col-span-5 relative max-w-[440px] w-full mx-auto lg:ml-auto">
        <TiltPortrait />
      </div>
    </div>
  </section>
);
